import React from "react";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme } from "@mui/joy";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { BasicModalContent, LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import _ from "lodash";
import { ServiceReminder } from "../../graphql/queries/serviceReminder";
import { UpdateServiceReminder } from "../../graphql/mutations/updateServiceReminder";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import { isDueDateAfter } from "../../utils/serviceReminderDateValidation";

interface ReminderLike {
    id: string;
    nextDueDate?: string | null;
    lastServiceDate?: string | null;
}

interface Props {
    open: boolean;
    reminder: ReminderLike;
    serviceName?: string;
    onClose: () => void;
}

const UpdateServiceReminderModal = ({ open, reminder, serviceName, onClose }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();

    const [updateServiceReminder] = useMutation(UpdateServiceReminder);

    // The edit form attributes (prefilled with current values) come straight off
    // the serviceReminder query's typeDefinitionAttributeList.
    const { data, loading } = useQuery(ServiceReminder, {
        variables: { id: reminder.id },
        skip: !reminder.id || !open,
        fetchPolicy: "network-only",
    });

    const serviceReminder = _.get(data, "serviceReminder");

    const attributes = _.get(serviceReminder, "typeDefinitionAttributeList.typeDefinitionAttributes", []);

    const initialFormValues = {
        lastServiceDate: _.get(serviceReminder, "lastServiceDate") ?? reminder.lastServiceDate ?? undefined,
        nextDueDate: _.get(serviceReminder, "nextDueDate") ?? reminder.nextDueDate ?? undefined,
    };

    const title = serviceName
        ? `${t("Edit service reminder")} – ${serviceName}`
        : t("Edit service reminder");

    const handleSubmit = (input: any) => {
        if (!isDueDateAfter(input.nextDueDate, input.lastServiceDate)) {
            toast.error(t("The next due date must be after the last service date."));
            return;
        }

        return updateServiceReminder({
            variables: { id: reminder.id, input },
            refetchQueries: ["ServiceReminderList"],
        })
            .then((res) => {
                if (res.data?.updateServiceReminder?.id) {
                    toast.success(t("Service reminder updated successfully."));
                    onClose();
                } else {
                    toast.error(t("Error updating service reminder."));
                }
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={"titleSmallLight"}>{title}</Typography>}
            >
                {loading ? (
                    <Box sx={{ py: "24px" }}>
                        <LoadingScreen size={"sm"} />
                    </Box>
                ) : (
                    <InstalledBaseTDAForm
                        formStyle={{ paddingTop: "0px" }}
                        onSubmit={handleSubmit}
                        onCancel={onClose}
                        submitButtonText={t("Save")}
                        attributes={attributes}
                        initialFormValues={initialFormValues}
                    />
                )}
            </BasicModalContent>
        </Dialog>
    );
};

export default UpdateServiceReminderModal;
